/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom'

import { styled, Theme, CSSObject } from '@mui/material'
import { Drawer as MuiDrawer } from '@mui/material'
import { List } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ListItem } from '@mui/material'
import { ListItemButton } from '@mui/material'
import { ListItemIcon } from '@mui/material'
import IconExpandLess from '@mui/icons-material/ExpandLess'
import IconExpandMore from '@mui/icons-material/ExpandMore'
import { Typography } from '@mui/material'
import { Box } from '@mui/material'
import { Icon, IconButton, Popover, Paper, ClickAwayListener } from '@mui/material'
import { Collapse } from '@mui/material'

import Text from '../typography/typography'
import images from '../../assets/images'

import './sidebar.scss'
import SearchBar from '../search-bar/search-bar'
import Tooltip from '../tooltip/Tooltip'
import { useLanguage } from '@erpsquad/common/runtime'
import DynamicSvgIcon from '../../assets/dynamic-svg-icon/dynamic-svg-icon'
import { Search, SearchStatus } from '../icons'
import { Pathname } from '../../constants/pathnames'
import { getApiConfig } from '@/utils'

// Types
interface MenuItem {
	label: string
	link: string
	icon?: string
	submenu?: MenuItem[]
	connectedRoutes?: string[]
}

interface CollapsedSubmenuState {
	open: boolean
	anchorEl: HTMLElement | null
	menu: MenuItem | null
	breadcrumb: MenuItem[]
}

let drawerWidth: any

const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	borderRight: `1px solid ${theme.palette.theme?.secondary[200]}`,
	backgroundColor: theme.palette.theme?.secondary[100],
})

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
	borderRight: `1px solid ${theme.palette.theme?.secondary[200]}`,
	backgroundColor: theme.palette.theme?.secondary[100],
})

const Drawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
	width: drawerWidth,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	'.MuiDrawer-paper': {
		backgroundColor: theme?.palette?.theme?.secondary?.[100],
		overflow: 'visible !important',
		zIndex: 1201,
	},
	...(open && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': openedMixin(theme),
	}),
	...(!open && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': closedMixin(theme),
	}),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: theme.spacing(0, 2),
	height: '60px',
}))

const SubmenuPopover = styled(Paper)(({ theme }) => ({
	minWidth: 250,
	maxWidth: 350,
	maxHeight: '80vh',
	overflowY: 'auto',
	backgroundColor: theme.palette.theme?.secondary?.[100] || theme.palette.background.paper,
	borderRadius: 12,
	boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
	border: `1px solid ${theme.palette.theme?.secondary?.[200] || theme.palette.divider}`,
	'&.MuiPaper-root': {
		transformOrigin: 'top left',
	},
	'&::-webkit-scrollbar': {
		width: 6,
	},
	'&::-webkit-scrollbar-track': {
		backgroundColor: 'transparent',
	},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: 'rgba(0, 0, 0, 0.2)',
		borderRadius: 3,
	},
}))

const BreadcrumbContainer = styled(Box)(({ theme }) => ({
	padding: theme.spacing(1, 2),
	borderBottom: `1px solid ${theme.palette.theme?.secondary?.[200] || theme.palette.divider}`,
	backgroundColor: theme.palette.theme?.secondary?.[50] || theme.palette.grey[50],
}))

// Enhanced CollapsedSubmenu component with nested support
const CollapsedSubmenu: React.FC<{
	menu: MenuItem
	anchorEl: HTMLElement | null
	open: boolean
	onClose: () => void
	onItemClick: (item: MenuItem) => void
	onSubmenuClick: (item: MenuItem, event: React.MouseEvent<HTMLElement>) => void
	getActiveClassIf: (paths: string[] | string) => boolean
	breadcrumb: MenuItem[]
	onBreadcrumbClick: (item: MenuItem, index: number) => void
}> = ({
	menu,
	anchorEl,
	open,
	onClose,
	onItemClick,
	onSubmenuClick,
	getActiveClassIf,
	breadcrumb,
	onBreadcrumbClick,
}) => {
		const renderBreadcrumb = () => {
			if (breadcrumb.length <= 1) return null

			return (
				<BreadcrumbContainer>
					<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
						{breadcrumb.map((item, index) => (
							<React.Fragment key={index}>
								{index > 0 && <ChevronRightIcon sx={{ fontSize: 14, color: 'theme.secondary.400' }} />}
								<Typography
									variant="caption"
									onClick={() => onBreadcrumbClick(item, index)}
									sx={{
										cursor: index < breadcrumb.length - 1 ? 'pointer' : 'default',
										color: index < breadcrumb.length - 1 ? 'primary.main' : 'theme.secondary.600',
										fontWeight: index === breadcrumb.length - 1 ? 600 : 400,
										fontSize: '0.75rem',
										'&:hover':
											index < breadcrumb.length - 1
												? {
													textDecoration: 'underline',
												}
												: {},
									}}
								>
									{item.label}
								</Typography>
							</React.Fragment>
						))}
					</Box>
				</BreadcrumbContainer>
			)
		}

		return (
			<ClickAwayListener onClickAway={onClose}>
				<Popover
					open={open}
					anchorEl={anchorEl}
					onClose={onClose}
					anchorOrigin={{
						vertical: 'center',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'center',
						horizontal: 'left',
					}}
					PaperComponent={SubmenuPopover}
					elevation={1}
					disableRestoreFocus
					disableAutoFocus
					disableEnforceFocus
					sx={{
						ml: 0.5,
						'& .MuiPopover-paper': {
							// overflow: 'visible',
							marginTop: 0,
							pointerEvents: 'auto',
							// border: '1px solid',
							// borderColor: 'theme.secondary.200',
						},
					}}
					transitionDuration={200}
				>
					<Box>
						{renderBreadcrumb()}

						<Box>
							<Typography
								variant="subtitle2"
								sx={{
									px: 2,
									py: 1,
									fontWeight: 600,
									color: 'theme.secondary.1000',
									borderBottom: breadcrumb.length > 1 ? 'none' : '1px solid',
									borderColor: 'theme.secondary.200',
									mb: 0.5,
									fontSize: '0.875rem',
								}}
							>
								{menu.label}
							</Typography>

							<List component="div" sx={{ py: 0 }}>
								{menu.submenu?.map((subItem: MenuItem, index: number) => {
									const isActive = getActiveClassIf([...(subItem?.connectedRoutes || []), ...[subItem.link]])

									return (
										<ListItem key={index} disablePadding>
											<ListItemButton
												onClick={(e) => {
													if (subItem.submenu?.length) {
														onSubmenuClick(subItem, e)
													} else {
														onItemClick(subItem)
														onClose()
													}
												}}
												selected={isActive}
												sx={(theme) => ({
													minHeight: 32,
													// px: 2,
													// py: 0.75,
													borderRadius: 1.5,
													// mx: 1,
													mb: 0.25,
													position: 'relative',
													'&.Mui-selected': {
														backgroundColor: theme.palette.theme?.primary[100],
														color: 'primary.main',
														'& .MuiListItemIcon-root': {
															color: 'primary.main',
														},
													},
													'&.Mui-selected:hover': {
														backgroundColor: theme.palette.theme?.primary[200],
													},
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.04)',
													},
												})}
											>
												{subItem.icon && (
													<ListItemIcon sx={{ minWidth: 32 }}>
														<img
															src={subItem.icon}
															alt={subItem.label + ' icon'}
															// width={20}
															// height={20}
															style={{ opacity: 0.8 }}
														/>
													</ListItemIcon>
												)}

												<Text
													type={'s3'}
													weight="normal"
													color={'theme.secondary.1000'}
													sx={{
														flexGrow: 1,
														fontSize: '0.875rem',
														lineHeight: 1.4,
													}}
												>
													{subItem.label}
												</Text>

												{subItem.submenu?.length > 0 && (
													<ChevronRightIcon
														sx={{
															fontSize: 18,
															color: 'theme.secondary.500',
															ml: 1,
														}}
													/>
												)}
											</ListItemButton>
										</ListItem>
									)
								})}
							</List>
						</Box>
					</Box>
				</Popover>
			</ClickAwayListener>
		)
	}

interface SidebarProps {
	width: number
	open: boolean
	menu: MenuItem[]
	theme: any
	forcedOpen: boolean
	handleMouseEnter: () => void
	handleMouseLeave: () => void
	handleDrawerClose: () => void
	handleDrawerOpen: () => void
	dashboardPath?: string
}

export const Sidebar: React.FC<SidebarProps> = (props) => {
	const navigate = useNavigate()
	const currentLocation = useLocation()
	const { currentLanguage } = useLanguage()
	const isRtl = useMemo(() => currentLanguage?.direction === 'rtl', [currentLanguage])
	const [collapsedSubmenuState, setCollapsedSubmenuState] = useState<CollapsedSubmenuState>({
		open: false,
		anchorEl: null,
		menu: null,
		breadcrumb: [],
	})

	const [menus, setMenus] = useState<MenuItem[]>([])
	const [search, setSearch] = useState<string>('')
	const [openSubmenuArray, setOpenSubmenuArray] = useState<string[]>([])
	const [isOpenSearchTooltop, setIsOpenSearchTooltop] = useState<boolean>(false)

	// Enhanced active class checker
	const getActiveClassIf = useCallback(
		(paths: string[] | string) => {
			const shouldMatch: { path: string }[] = Array.isArray(paths)
				? paths.map((path: string) => ({ path }))
				: [{ path: paths }]

			return Boolean(matchRoutes(shouldMatch, currentLocation))
		},
		[currentLocation]
	)

	// Find if any submenu item is active (for parent selection when collapsed)
	const isSubmenuActive = useCallback(
		(menu: MenuItem): boolean => {
			if (!menu.submenu?.length) return false

			for (const subItem of menu.submenu) {
				const isActive = getActiveClassIf([...(subItem?.connectedRoutes || []), ...[subItem.link]])

				if (isActive || isSubmenuActive(subItem)) {
					return true
				}
			}

			return false
		},
		[getActiveClassIf]
	)

	// Get all menu paths for a menu item (including nested)
	const getAllMenuPaths = useCallback((menu: MenuItem): string[] => {
		const paths: string[] = []

		if (menu.link) {
			paths.push(menu.link)
		}

		if (menu.connectedRoutes?.length) {
			paths.push(...menu.connectedRoutes)
		}

		if (menu.submenu?.length) {
			menu.submenu.forEach((subItem) => {
				paths.push(...getAllMenuPaths(subItem))
			})
		}

		return paths
	}, [])

	// Enhanced collapsed menu click handler
	const handleCollapsedMenuClick = useCallback(
		(event: React.MouseEvent<HTMLElement>, menu: MenuItem) => {
			event.stopPropagation()

			if (!props.open && menu.submenu?.length > 0) {
				if (collapsedSubmenuState.open && collapsedSubmenuState.menu?.label === menu.label) {
					setCollapsedSubmenuState({
						open: false,
						anchorEl: null,
						menu: null,
						breadcrumb: [],
					})
				} else {
					setCollapsedSubmenuState({
						open: true,
						anchorEl: event.currentTarget,
						menu: menu,
						breadcrumb: [menu],
					})
				}
			}
		},
		[props.open, collapsedSubmenuState]
	)

	// Handle submenu navigation in collapsed mode
	const handleCollapsedSubmenuClick = useCallback((item: MenuItem, event: React.MouseEvent<HTMLElement>) => {
		if (item.submenu?.length) {
			setCollapsedSubmenuState((prev) => ({
				...prev,
				menu: item,
				breadcrumb: [...prev.breadcrumb, item],
			}))
		}
	}, [])

	// Handle breadcrumb navigation
	const handleBreadcrumbClick = useCallback((item: MenuItem, index: number) => {
		setCollapsedSubmenuState((prev) => ({
			...prev,
			menu: item,
			breadcrumb: prev.breadcrumb.slice(0, index + 1),
		}))
	}, [])

	const handleCollapsedSubmenuClose = useCallback(() => {
		setCollapsedSubmenuState({
			open: false,
			anchorEl: null,
			menu: null,
			breadcrumb: [],
		})
	}, [])

	const handleSubmenuItemClick = useCallback(
		(item: MenuItem) => {
			navigate(item.link)
		},
		[navigate]
	)

	// Enhanced menu click handler
	const handleMenuClick = useCallback(
		(menu: MenuItem, parent: MenuItem | null, index: number) => {
			if (!props.open && menu.submenu?.length > 0) {
				return
			}

			if (!menu.submenu?.length) {
				navigate(menu.link)
				if (!parent) {
					setOpenSubmenuArray([])
				}
			} else if (menu.submenu?.length && menu.submenu[index]?.link) {
				navigate(`${menu.link}${menu.submenu[index].link}`)
			} else {
				const menuKey = `${parent?.label || ''}-${menu.label}`

				if (openSubmenuArray.includes(menuKey)) {
					if (parent) {
						const newSubArray = openSubmenuArray.filter((f) => f !== menuKey)
						setOpenSubmenuArray([...newSubArray])
					} else {
						setOpenSubmenuArray([])
					}
				} else {
					if (parent) {
						setOpenSubmenuArray([...openSubmenuArray, menuKey])
					} else {
						setOpenSubmenuArray([menuKey])
					}
				}
			}
		},
		[props.open, navigate, openSubmenuArray]
	)

	// Enhanced search functionality
	const filterMenuOnSearch = useCallback((menu: MenuItem[], search: string): MenuItem[] => {
		return menu
			.filter((m: MenuItem) => {
				if (m?.label?.toLowerCase()?.includes(search)) {
					return m
				} else if (m?.submenu?.length) {
					m.submenu = filterMenuOnSearch(m?.submenu, search)
					return m.submenu?.length > 0 ? m : null
				}
				return null
			})
			.filter(Boolean)
	}, [])

	const handleSidebarSearch = useCallback(
		(search: string) => {
			if (!search) {
				setMenus(props.menu || [])
			} else {
				const searchTerm = search.toLowerCase()
				const menuItems = props?.menu ? JSON.parse(JSON.stringify(props.menu)) : []
				const filteredMenu = filterMenuOnSearch(menuItems, searchTerm)
				setMenus(filteredMenu)
			}
		},
		[props.menu, filterMenuOnSearch]
	)

	const handleOpenSearchTooltip = useCallback(() => setIsOpenSearchTooltop(true), []);
	const handleCloseSearchTooltip = useCallback(() => setIsOpenSearchTooltop(false), []);

	// Enhanced menu renderer with better nested support
	const renderMenu = useCallback(
		(menus: MenuItem[], parent: MenuItem | null, level: number) => {
			return menus?.map((menu: MenuItem, index: number) => {
				const menuKey = `${parent?.label || ''}-${menu.label}`
				const isMenuActive = getActiveClassIf([...(menu?.connectedRoutes || []), ...[menu.link]])

				// Check if any nested submenu is active (for collapsed state parent selection)
				const hasActiveSubmenu = !props.open && isSubmenuActive(menu)
				const shouldHighlightParent = isMenuActive || hasActiveSubmenu

				return (
					<Tooltip
						title={menu.label}
						placement="right"
						arrow
						key={`${menu.label}-${index}`}
						disableHoverListener={props.open}
						disableFocusListener={props.open}
						disableTouchListener={props.open}
					>
						<ListItem
							disablePadding
							sx={{
								display: 'block',
								// mb: 0.5,
								...(isRtl && {
									padding: 0,
								}),
							}}
						>
							<ListItemButton
								onClick={(e) => {
									if (!props.open && menu.submenu?.length > 0) {
										handleCollapsedMenuClick(e, menu)
									} else {
										handleMenuClick(menu, parent, menu?.submenu ? -1 : index)
									}
								}}
								selected={shouldHighlightParent}
								sx={(theme) => ({
									minHeight: props.open ? 32 : 32,
									justifyContent: props.open ? 'initial' : 'center',
									display: 'flex',
									alignItems: 'center',
									borderRadius: 2,
									gap: 1,
									// mx: props.open ? 1 : 0.5,
									// px: props.open ? 2 : 1,
									// py: 1,
									position: 'relative',
									transition: 'all 0.2s ease-in-out',

									'&.Mui-selected': {
										backgroundColor: theme.palette.theme?.primary[100],
										color: theme.palette.theme?.primary[800],
										'& .MuiListItemIcon-root': {
											color: theme.palette.theme?.primary[800],
										},
										'& .MuiTypography-root': {
											color: theme.palette.theme?.primary[800],
											fontWeight: 500,
										},
									},
									'&.Mui-selected:hover': {
										backgroundColor: theme.palette.theme?.primary[200],
									},
									'&:hover': {
										backgroundColor: props.open ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.08)',
										transform: props.open ? 'none' : 'scale(1.02)',
									},

									...(!props.open &&
										menu.submenu?.length > 0 && {
										cursor: 'pointer',
										'&::after': {
											content: '""',
											position: 'absolute',
											...(isRtl
												? {
													left: 8,
													borderRight: '5px solid',
												}
												: {
													right: 8,
													borderLeft: '5px solid',
												}),
											top: '25%',
											transform: 'translateY(-50%)',
											width: 0,
											height: 0,
											borderTop: '4px solid transparent',
											borderBottom: '4px solid transparent',
											borderColor: 'currentColor transparent transparent transparent',
											opacity: 0.7,
										},
									}),
								})}
							>
								<ListItemIcon
									sx={{
										minWidth: props.open ? 'auto' : 0,
										// mr: props.open ? 1 : 'auto',
										...(isRtl ? { ml: 0 } : {}),
										justifyContent: 'center',
										display: 'flex',
										alignItems: 'center',
										transition: 'all 0.2s ease-in-out',
									}}
								>
									{menu.icon ? (
										// <DynamicSvgIcon svgPath={menu.icon} color={shouldHighlightParent ? 'primary' : 'grey'} fontSize={props.open ? 'small' : 'medium'} />
										<img src={menu.icon} alt={menu.label + ' icon'} width={20} height={20} style={{ opacity: shouldHighlightParent ? 1 : 0.7 }} />
									) : (
										<ChevronRightIcon
											sx={{
												fontSize: props.open ? 24 : 28,
												transition: 'all 0.2s ease-in-out',
											}}
										/>
									)}
								</ListItemIcon>

								{props.open && (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											flexGrow: 1,
											position: 'relative',
											overflow: 'hidden',
										}}
									>
										<Text
											type={'s3'}
											weight={level === 0 ? 'medium' : 'normal'}
											color={'theme.secondary.1000'}
											sx={{
												flexGrow: 1,
												pl: level * 1.5,
												fontSize: level === 0 ? '0.875rem' : '0.8125rem',
												lineHeight: 1.4,
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{menu.label}
										</Text>

										{menu.submenu?.length > 0 && (
											<Box
												sx={{
													ml: 1,
													display: 'flex',
													alignItems: 'center',
													transition: 'transform 0.2s ease-in-out',
												}}
											>
												{openSubmenuArray.includes(menuKey) ? (
													<IconExpandLess
														sx={{
															fontSize: 20,
															color: 'theme.secondary.600',
														}}
													/>
												) : (
													<IconExpandMore
														sx={{
															fontSize: 20,
															color: 'theme.secondary.600',
														}}
													/>
												)}
											</Box>
										)}
									</Box>
								)}
							</ListItemButton>

							{openSubmenuArray.includes(menuKey) && menu.submenu?.length > 0 && props.open && (
								<Collapse in={openSubmenuArray.includes(menuKey)} timeout="auto" unmountOnExit>
									<List component="div" disablePadding sx={{ mt: 0.5 }}>
										{renderMenu(menu.submenu, menu, level + 1)}
									</List>
								</Collapse>
							)}
						</ListItem>
					</Tooltip>
				)
			})
		},
		[props.open, openSubmenuArray, getActiveClassIf, handleCollapsedMenuClick, handleMenuClick, isSubmenuActive, isRtl]
	)

	// Enhanced useEffect for auto-expanding active menus
	useEffect(() => {
		handleSidebarSearch(search)

		if (props.menu?.length) {
			const expandedMenus: string[] = []

			const checkAndExpandMenu = (menuItems: MenuItem[], parentLabel = '') => {
				menuItems.forEach((menu) => {
					if (menu?.submenu?.length) {
						const menuKey = `${parentLabel}-${menu.label}`
						const allPaths = getAllMenuPaths(menu)

						if (getActiveClassIf(allPaths)) {
							expandedMenus.push(menuKey)
							checkAndExpandMenu(menu.submenu, menu.label)
						}
					}
				})
			}

			checkAndExpandMenu(props.menu)
			setOpenSubmenuArray(expandedMenus)
		}
	}, [getActiveClassIf, handleSidebarSearch, props.menu, search, getAllMenuPaths])

	useEffect(() => {
		if (props.open) {
			handleCollapsedSubmenuClose()
		}
	}, [props.open, handleCollapsedSubmenuClose])

	drawerWidth = props.width

	const appLogo = getApiConfig().appLogo || images.logo
	const appFavicon = getApiConfig().appFavicon || images.favicon

	return (
		<>
			<Drawer
				variant="permanent"
				open={props.open}
				anchor={document.body.dir === 'rtl' ? 'right' : 'left'}
				onMouseEnter={props.handleMouseEnter}
				onMouseLeave={props.handleMouseLeave}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						backgroundColor: 'grey.100',
					}}
				>
					{/* Header */}
					<Box
						sx={(theme) => ({
							borderBottom: `1px solid ${theme.palette.theme?.secondary?.[200] || theme.palette.divider}`,
						})}
					>
						<DrawerHeader>
							<img
								src={props.open ? appLogo : appFavicon}
								alt={props.open ? 'logo' : 'favicon'}
								width={!props.open && 40}
								height={55}
								style={{
									transition: 'all 0.3s ease-in-out',
									filter: 'brightness(1.1)',
									cursor: 'pointer',
								}}
								onClick={() => navigate(props.dashboardPath || Pathname.DASHBOARD)}
							/>
						</DrawerHeader>
					</Box>

					{/* Search Bar */}
					{props.open ? (
						<Box sx={{ px: 1.5, py: 1.5, borderBottom: '1px solid', borderColor: 'theme.secondary.200' }}>
							<SearchBar className="sidebar-search" handleSearch={setSearch} placeholder="Search menu..." value={search} sx={{ '&.MuiTextField-root': { backgroundColor: 'grey.200', border: '1px solid', borderColor: 'grey.200', borderRadius: 1 } }} />
						</Box>
					) : (
						<ClickAwayListener onClickAway={handleCloseSearchTooltip}>
							<Box sx={{ p: 1, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<Tooltip
									title={
										<SearchBar
											className="sidebar-search"
											handleSearch={setSearch}
											placeholder="Search menu..."
											value={search}
											sx={{ '&.MuiTextField-root': { backgroundColor: 'transparent' } }}
										/>
									}
									placement="right"
									arrow
									sx={{
										p: 0,
										backgroundColor: 'grey.200',
										border: '1px solid',
										borderColor: 'grey.300',
										'& .MuiTooltip-arrow': {
											color: 'grey.300',
										},
										ml: '8px !important'
									}}
									open={isOpenSearchTooltop}
								>
									<IconButton color='inherit' sx={{ borderRadius: 2, p: 1.5, backgroundColor: isOpenSearchTooltop ? 'grey.200' : 'inherit' }} onClick={handleOpenSearchTooltip}>
										{search.trim() ? <SearchStatus /> : <Search />}
									</IconButton>
								</Tooltip>
							</Box>
						</ClickAwayListener>
					)}

					{/* Menu List */}
					<Box
						sx={{
							flexGrow: 1,
							overflowY: 'auto',
							py: 1,
							'&::-webkit-scrollbar': {
								width: 6,
							},
							'&::-webkit-scrollbar-track': {
								backgroundColor: 'transparent',
							},
							'&::-webkit-scrollbar-thumb': {
								backgroundColor: 'rgba(0, 0, 0, 0.2)',
								borderRadius: 3,
							},
						}}
					>
						<List className="sidebar-list" sx={{ p: 0 }}>
							{renderMenu(menus, null, 0)}
						</List>
					</Box>

					{/* Footer */}
					{props.open && (
						<Box
							sx={{
								borderTop: '1px solid',
								borderColor: 'theme.secondary.200',
								backgroundColor: 'rgba(255, 255, 255, 0.5)',
							}}
						>
							<Box
								sx={{
									padding: '.75rem 1.5rem',
									display: 'flex',
									alignItems: 'self-start',
									gap: 0.5,
									justifyContent: 'center',
								}}
							>
								<Typography
									sx={{
										color: 'theme.secondary.600',
										fontSize: '.85rem',
										fontWeight: 500,
									}}
								>
									Crafted with
								</Typography>
								<Icon
									sx={{
										color: 'theme.error.500',
										width: '1rem',
										height: '1rem',
										fontSize: '.75rem',
									}}
								>
									❤️
								</Icon>
								<Typography
									sx={{
										color: 'theme.secondary.600',
										fontSize: '.75rem',
										fontWeight: 500,
									}}
								>
									<img src={images.erp_logo} />
								</Typography>
							</Box>
						</Box>
					)}
				</Box>
			</Drawer>
			<IconButton
				size="small"
				onClick={() => (props.forcedOpen ? props.handleDrawerClose() : props.handleDrawerOpen())}
				className={isRtl ? 'drawer-expand-btn_rtl' : 'drawer-expand-btn'}
				sx={{
					ml: 'auto',
					backgroundColor: 'rgba(0, 0, 0, 0.04)',
					'&:hover': {
						backgroundColor: 'grey.100',
					},
					// transition: 'all 0.2s ease-in-out',
					width: '30px',
					height: '30px',
					borderRadius: `8px`,
					borderTopLeftRadius: 0,
					borderBottomLeftRadius: 0,
					borderColor: 'grey.500',
					...(isRtl
						? !props.forcedOpen
							? { right: '63px !important', left: 'auto' }
							: { right: '299px', left: 'auto' }
						: !props.forcedOpen
							? { left: '63px !important', right: 'auto' }
							: { left: '299px', right: 'auto' }),
					transition: 'all 0.3s ease-in-out',
				}}
			>
				{props.theme.direction === 'rtl' ? (
					props.forcedOpen ? (
						<ChevronRightIcon fontSize="small" />
					) : (
						<ChevronLeftIcon fontSize="small" />
					)
				) : props.forcedOpen ? (
					<ChevronLeftIcon fontSize="small" />
				) : (
					<ChevronRightIcon fontSize="small" />
				)}
			</IconButton>
			{/* Collapsed Submenu Popover */}
			{collapsedSubmenuState.menu && (
				<CollapsedSubmenu
					menu={collapsedSubmenuState.menu}
					anchorEl={collapsedSubmenuState.anchorEl}
					open={collapsedSubmenuState.open}
					onClose={handleCollapsedSubmenuClose}
					onItemClick={handleSubmenuItemClick}
					onSubmenuClick={handleCollapsedSubmenuClick}
					getActiveClassIf={getActiveClassIf}
					breadcrumb={collapsedSubmenuState.breadcrumb}
					onBreadcrumbClick={handleBreadcrumbClick}
				/>
			)}
		</>
	)
}

export default Sidebar
